import { unwrapResponse, wrapRequest } from "../api/crypto";
import { apiHeadersJson } from "../api/headers";
import { useState } from "react";
import { Link } from "react-router-dom";
import type { RagQueryResult } from "../api/types";
import { SearchIcon } from "./Icons";
import { Empty } from "./States";
import { fmtMs } from "../lib/format";

/**
 * Run a retrieval query against the live index.
 *
 * The only action in the Platform section that calls out rather than reading a
 * projection, and deliberately the only one: `/api/v1/rag/query` retrieves and
 * does not write. MCP's `execute` is not surfaced anywhere here — it dispatches
 * tools that write, and that needs its own permission rather than the session
 * that reads dashboards.
 *
 * It answers a question the collection counts cannot: 97 chunks in an index
 * says nothing about whether a search for something real comes back with the
 * right passage.
 */
/**
 * Collapse OCR whitespace into readable prose.
 *
 * Snippets come from a PDF text layer, where every visual line break is a
 * newline and column gaps are runs of spaces. Rendered verbatim they produce a
 * tall ladder of one- and two-word lines. The line breaks carry no meaning
 * worth preserving in a result list, so they become single spaces.
 */
const tidy = (s: unknown): string => String(s ?? "").replace(/\s+/g, " ").trim();

export function RagQueryProbe({ collections }: { collections: string[] }) {
    const [query, setQuery] = useState("");
    const [collection, setCollection] = useState("");
    const [topK, setTopK] = useState(5);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [res, setRes] = useState<RagQueryResult | null>(null);

    const run = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!query.trim()) return;
        setBusy(true);
        setError(null);
        setRes(null);
        try {
            const r = await fetch("/api/orchestrator/platform/rag/query", {
                method: "POST",
                headers: apiHeadersJson(),
                body: await wrapRequest({
                    query: query.trim(),
                    collection: collection || undefined,
                    top_k: topK,
                }),
            });
            const body = await unwrapResponse<RagQueryResult>(await r.json());
            if (!r.ok && !body?.error) throw new Error(`Query failed (${r.status}).`);
            setRes(body);
            if (body.error) setError(body.error);
        } catch (err) {
            setError(err instanceof Error ? err.message : String(err));
        } finally {
            setBusy(false);
        }
    };

    const citations = res?.result?.citations ?? [];

    return (
        <div className="card">
            <div className="card-head bordered">
                <span className="card-title">Try a query</span>
                <span className="muted">read-only retrieval against the live index</span>
            </div>
            <div className="card-body">
                <form onSubmit={run} className="controls" style={{ marginBottom: 4 }}>
                    <span className="search-wrap" style={{ flex: 1, minWidth: 260 }}>
                        <span className="search-icon"><SearchIcon /></span>
                        <input
                            type="search"
                            className="search-input"
                            style={{ width: "100%" }}
                            placeholder="e.g. trade licence expiry date"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            aria-label="Retrieval query"
                        />
                    </span>

                    <label className="muted" htmlFor="rq-col">Collection</label>
                    <select
                        id="rq-col"
                        value={collection}
                        onChange={(e) => setCollection(e.target.value)}
                    >
                        <option value="">All</option>
                        {collections.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>

                    <label className="muted" htmlFor="rq-k">Top K</label>
                    <select id="rq-k" value={topK} onChange={(e) => setTopK(Number(e.target.value))}>
                        {[3, 5, 10, 20].map((n) => <option key={n} value={n}>{n}</option>)}
                    </select>

                    <button type="submit" className="btn-primary" disabled={busy || !query.trim()}>
                        {busy ? "Searching…" : "Search"}
                    </button>
                </form>

                {error ? (
                    <div className="notice notice-warn" style={{ marginTop: 16, marginBottom: 0 }}>
                        {error}
                    </div>
                ) : null}

                {res && !error ? (
                    <div style={{ marginTop: 20 }}>
                        <div className="muted" style={{ marginBottom: 12 }}>
                            {citations.length} passage{citations.length === 1 ? "" : "s"} in{" "}
                            {fmtMs(res.latency_ms)}
                        </div>

                        {res.result?.answer ? (
                            <div className="notice" style={{ marginBottom: 16 }}>
                                <strong>Composed answer.</strong>{" "}
                                {/* Labelled as composed, not as a fact: it is
                                    assembled from the passages below, and the
                                    passages are the evidence.

                                    Whitespace is collapsed rather than preserved
                                    — the newlines come from PDF page layout, and
                                    honouring them turned this block into a wall
                                    of one-word lines. */}
                                <div className="snippet" style={{ marginTop: 8, maxWidth: "none" }}>
                                    {tidy(res.result.answer)}
                                </div>
                            </div>
                        ) : null}

                        {citations.length === 0 ? (
                            <Empty
                                title="Nothing matched"
                                hint="The index returned no passage above its similarity threshold for this query."
                            />
                        ) : (
                            <div className="table-wrap">
                                <table>
                                    <thead>
                                        <tr>
                                            <th scope="col" className="si">Sl</th>
                                            {/* Similarity sits beside the passage: the
                                                score is only meaningful read against the
                                                text it scored, and it was previously at
                                                the far right edge of a 1900px row. */}
                                            <th scope="col" className="num">Score</th>
                                            <th scope="col" className="cell-prose">Passage</th>
                                            <th scope="col">Document</th>
                                            <th scope="col" className="num">Page</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {citations.map((c, i) => (
                                            <tr key={`${c.document_id}-${c.chunk_index}-${i}`}>
                                                <td className="si">{i + 1}</td>
                                                <td className="num">
                                                    {typeof c.similarity_score === "number" ? (
                                                        <span
                                                            className={`pill ${
                                                                c.similarity_score >= 0.9 ? "pill-good"
                                                                : c.similarity_score >= 0.7 ? "pill-warning"
                                                                : ""
                                                            }`}
                                                        >
                                                            {c.similarity_score.toFixed(3)}
                                                        </span>
                                                    ) : (
                                                        "—"
                                                    )}
                                                </td>
                                                <td className="cell-prose">
                                                    {/* title carries the untruncated text, so
                                                        clamping loses nothing. */}
                                                    <div
                                                        className="snippet"
                                                        title={c.snippet ? tidy(c.snippet) : undefined}
                                                    >
                                                        {c.snippet ? tidy(c.snippet) : "—"}
                                                    </div>
                                                </td>
                                                <td className="nowrap">
                                                    {c.document_id ? (
                                                        <Link
                                                            to={`/documents/${c.document_id}`}
                                                            className="mono"
                                                            title={c.document_id}
                                                        >
                                                            {c.document_id.slice(0, 8)}…
                                                        </Link>
                                                    ) : "—"}
                                                </td>
                                                <td className="num">{c.page_number ?? "—"}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                ) : null}
            </div>
        </div>
    );
}
